(function () {
  // THIS IS THE MINIMUM ORDER BIT.
  // ----------------------- Perf helpers ------------------------------------
  const TTL_MS = 800; // cart cache TTL
  let cartCache = { at: 0, data: null, inFlight: null };

  async function getCartCached(force = false) {
    const now = Date.now();
    if (!force && cartCache.data && (now - cartCache.at) < TTL_MS) return cartCache.data;
    if (cartCache.inFlight) return cartCache.inFlight;

    cartCache.inFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(json => {
        cartCache = { at: Date.now(), data: json, inFlight: null };
        return json;
      })
      .catch(e => { cartCache.inFlight = null; throw e; });

    return cartCache.inFlight;
  }

  function debounce(fn, wait = 200) {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  }

  // ------------------- DOM: rules & targets --------------------------------
  function collectRules() {
    // Unique by productId, prefer title, fallback to handle
    const map = new Map();
    document.querySelectorAll('#min-variant-mix').forEach(el => {
      const min = parseInt(el.dataset.min || '0', 10);
      const pid = el.dataset.productId;
      const handle = el.dataset.productHandle || '';
      const title = el.dataset.productTitle || '';
      if (pid && min > 0 && !map.has(pid)) {
        map.set(pid, { productId: pid, handle, title, min });
      }
    });
    return Array.from(map.values());
  }

  // Checkout buttons + banners (cart page & drawer)
  let checkoutBtns = [];
  let checkoutBanners = new WeakMap(); // btn -> banner div

  function scanCheckoutButtons() {
    checkoutBtns = Array.from(document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], a[href^="/checkout"], .cart__checkout, .btn-checkout, .button-checkout'
    ));
    checkoutBtns.forEach(btn => {
      if (!checkoutBanners.has(btn)) {
        const b = document.createElement('div');
        b.className = 'min-mix-banner-cart';
        b.style.cssText = 'display:none;margin-top:10px;font-size:14px;padding:8px;border:1px solid #f1c40f;background:#fff8e1;border-radius:6px;';
        btn.insertAdjacentElement('afterend', b);
        checkoutBanners.set(btn, b);
      }
    });
  }

  // Product page banner (under Add to Cart) — NEVER on /cart
  function getProductBanner() {
    if (window.location.pathname.startsWith('/cart')) return null; // don't show on cart page
    let el = document.getElementById('min-mix-banner');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'min-mix-banner';
    el.style.cssText = 'display:none;margin-top:10px;font-size:14px;padding:8px;border:1px solid #f1c40f;background:#fff8e1;border-radius:6px;';
    const addBtn = document.querySelector('form[action^="/cart/add"] [type="submit"], form[action="/cart/add"] [type="submit"]');
    (addBtn && addBtn.parentNode ? addBtn.parentNode : document.body).insertBefore(el, addBtn ? addBtn.nextSibling : null);
    return el;
  }

  // -------------------- Core evaluation ------------------------------------
  function summarizeUnmet(cart, rules) {
    // Count qty per product id once
    const counts = {};
    for (const it of cart.items) {
      const pid = String(it.product_id);
      counts[pid] = (counts[pid] || 0) + it.quantity;
    }
    const unmet = [];
    for (const r of rules) {
      const have = counts[String(r.productId)] || 0;
      if (have < r.min) {
        unmet.push({
          productId: r.productId,
          min: r.min,
          have,
          need: r.min - have,
          // Prefer title, fallback to handle prettified
          name: (r.title && r.title.trim()) ? r.title : (r.handle || '').replace(/-/g, ' ')
        });
      }
    }
    return unmet;
  }

  // -------------------- Rendering ------------------------------------------
  function renderProductNotice(unmetForThisProduct, min, displayName) {
    const banner = getProductBanner();
    if (!banner) return; // no banner on cart page
    if (unmetForThisProduct > 0) {
      banner.style.display = '';
      banner.innerHTML =
        `Please add <strong>${unmetForThisProduct}</strong> more item(s) from <strong>${displayName}</strong>. ` +
        `A minimum of <strong>${min}</strong> items are required of this item including any variants.`;
    } else {
      banner.style.display = 'none';
      banner.innerHTML = '';
    }
  }

  function renderCartNotices(unmetList) {
    const hasUnmet = unmetList.length > 0;
    const msg = hasUnmet
      ? unmetList.map(u =>
          `Please add <strong>${u.need}</strong> more item(s) from <strong>${u.name}</strong>. ` +
          `A minimum of <strong>${u.min}</strong> items are required of this item including any variants.`
        ).join('<br>')
      : '';

    checkoutBtns.forEach(btn => {
      if (hasUnmet) {
        btn.setAttribute('disabled','disabled');
        btn.classList.add('is-disabled');
      } else {
        btn.removeAttribute('disabled');
        btn.classList.remove('is-disabled');
      }
      const b = checkoutBanners.get(btn);
      if (b) {
        b.style.display = hasUnmet ? '' : 'none';
        b.innerHTML = msg;
      }
    });
  }

  // -------------------- Update cycle (debounced) ----------------------------
  const updateAll = debounce(async function () {
    scanCheckoutButtons();
    const rules = collectRules();

    if (!rules.length) {
      // No rules active → enable checkout & hide banners; no cart fetch needed
      checkoutBtns.forEach(btn => { btn.removeAttribute('disabled'); btn.classList.remove('is-disabled'); });
      checkoutBtns.forEach(btn => { const b = checkoutBanners.get(btn); if (b) { b.style.display = 'none'; b.innerHTML = ''; } });
      const pb = document.getElementById('min-mix-banner'); if (pb) { pb.style.display = 'none'; pb.innerHTML = ''; }
      return;
    }

    const cart = await getCartCached();
    const unmet = summarizeUnmet(cart, rules);

    // Product page message (only if we're on a product page with config)
    const meta = document.getElementById('min-variant-mix');
    if (meta && !window.location.pathname.startsWith('/cart')) {
      const pid = meta.dataset.productId;
      const min = parseInt(meta.dataset.min || '0', 10);
      const title = meta.dataset.productTitle || '';
      const handle = meta.dataset.productHandle || '';
      const displayName = (title && title.trim()) ? title : handle.replace(/-/g, ' ');
      const entry = unmet.find(u => String(u.productId) === String(pid));
      renderProductNotice(entry ? entry.need : 0, min, displayName);
    }

    // Cart/drawer messages + checkout toggle
    renderCartNotices(unmet);
  }, 150);

  // -------------------- Event wiring ---------------------------------------
  updateAll();

  // Quantity changes (cart page / drawer)
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (t && t.matches('input[name="updates[]"], input.qty, .cart-qty__input, .quantity__input')) {
      setTimeout(() => { cartCache.at = 0; updateAll(); }, 250);
    }
  }, { passive: true });

  // After add-to-cart clicks (product page)
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t && (t.matches('form[action^="/cart/add"] [type="submit"]') || t.closest('form[action^="/cart/add"] [type="submit"]'))) {
      setTimeout(() => { cartCache.at = 0; updateAll(); }, 500);
    }
  }, { passive: true });

  // DOM mutations (drawer open / redraw)
  const mo = new MutationObserver(() => { updateAll(); });
  mo.observe(document.documentElement, { childList: true, subtree: true });

})();
